import { Globe, Linkedin, Phone } from "lucide-react";
import { useGetIdentity, useRecordContext } from "ra-core";
import { EditButton } from "@/components/admin/edit-button";
import { DeleteButton } from "@/components/admin/delete-button";
import { PhoneField } from "@/components/admin/phone-field";
import { ReferenceField } from "@/components/admin/reference-field";
import { ShowButton } from "@/components/admin/show-button";
import { TextField } from "@/components/admin/text-field";
import { DateField } from "@/components/admin/date-field";
import { UrlField } from "@/components/admin/url-field";
import { SelectField } from "@/components/admin/select-field";
import { mapsHref } from "@/lib/linking";

import { AsideSection } from "../misc/AsideSection";
import { useConfigurationContext } from "../root/ConfigurationContext";
import { useCanViewAmounts } from "@/lib/permissions/useMaskedAmount";
import { OrganizationMemberName } from "../organizationMembers/OrganizationMemberName";
import type { Company } from "../types";
import { sizes } from "./sizes";
import { canUseCrmPermission } from "../providers/commons/crmPermissions";
import { HostingerCompanyDomainsSection } from "@/modules/hostinger/HostingerCompanyDomainsSection";

interface CompanyAsideProps {
  link?: string;
}

export const CompanyAside = ({ link = "edit" }: CompanyAsideProps) => {
  const record = useRecordContext<Company>();
  const { data: identity } = useGetIdentity();
  const canManageSales = canUseCrmPermission(identity as any, "sales.manage");
  if (!record) return null;

  return (
    <div className="hidden sm:block w-92 min-w-92 space-y-4">
      <div className="flex flex-row space-x-1">
        {link === "edit" && canManageSales ? (
          <EditButton label="Edit Company" />
        ) : !canManageSales ? null : (
          <ShowButton label="Show Company" />
        )}
      </div>

      <CompanyInfo record={record} />

      <AddressInfo record={record} />

      <ContextInfo record={record} />

      <HostingerCompanyDomainsSection companyId={Number(record.id)} />

      <AdditionalInfo record={record} />

      {link !== "edit" && canManageSales ? (
        <div className="mt-6 pt-6 border-t hidden sm:flex flex-col gap-2 items-start">
          <DeleteButton
            className="h-6 cursor-pointer hover:bg-destructive/10! text-destructive! border-destructive! focus-visible:ring-destructive/20 dark:focus-visible:ring-destructive/40"
            size="sm"
          />
        </div>
      ) : null}
    </div>
  );
};

export const CompanyInfo = ({ record }: { record: Company }) => {
  if (!record.website && !record.linkedin_url && !record.phone_number) {
    return null;
  }

  return (
    <AsideSection title="Company Info">
      {record.website && (
        <div className="flex flex-row items-center gap-1 min-h-[24px]">
          <Globe className="w-4 h-4" />
          <UrlField
            source="website"
            target="_blank"
            rel="noopener"
            content={record.website
              .replace("http://", "")
              .replace("https://", "")}
          />
        </div>
      )}
      {record.linkedin_url && (
        <div className="flex flex-row items-center gap-1 min-h-[24px]">
          <Linkedin className="w-4 h-4" />
          <a
            className="link-action"
            href={record.linkedin_url}
            target="_blank"
            rel="noopener noreferrer"
            title={record.linkedin_url}
          >
            LinkedIn
          </a>
        </div>
      )}
      {record.phone_number && (
        <div className="flex flex-row items-center gap-1 min-h-[24px]">
          <Phone className="w-4 h-4" />
          <PhoneField source="phone_number" />
        </div>
      )}
    </AsideSection>
  );
};

export const ContextInfo = ({ record }: { record: Company }) => {
  const { companySectors } = useConfigurationContext();
  const canViewAmounts = useCanViewAmounts();
  if (!record.revenue && !record.id) {
    return null;
  }

  const sectorLabel = companySectors.find(
    (s) => s.value === record.sector,
  )?.label;

  return (
    <AsideSection title="Context">
      {sectorLabel && <span>Sector: {sectorLabel}</span>}
      {record.size && (
        <span>
          Size: <SelectField source="size" choices={sizes} />
        </span>
      )}
      {record.revenue && canViewAmounts ? (
        <span>
          Revenue: <TextField source="revenue" />
        </span>
      ) : null}
      {record.revenue && !canViewAmounts ? <span>Revenue: —</span> : null}
      {record.tax_identifier && (
        <span>
          Tax Identifier: <TextField source="tax_identifier" />
        </span>
      )}
    </AsideSection>
  );
};

export const AddressInfo = ({ record }: { record: Company }) => {
  if (
    !record.address &&
    !record.city &&
    !record.zipcode &&
    !record.state_abbr
  ) {
    return null;
  }

  return (
    <AsideSection title="Main Address" noGap>
      <a
        href={mapsHref(
          [
            record.address,
            record.city,
            record.state_abbr,
            record.zipcode,
            record.country,
          ]
            .filter(Boolean)
            .join(" "),
        )}
        target="_blank"
        rel="noreferrer"
        className="link-action text-sm"
        title={[
          record.address,
          record.city,
          record.state_abbr,
          record.zipcode,
          record.country,
        ]
          .filter(Boolean)
          .join(", ")}
      >
        <TextField source="address" />
        <TextField source="city" />
        <TextField source="zipcode" />
        <TextField source="state_abbr" />
        <TextField source="country" />
      </a>
    </AsideSection>
  );
};

export const AdditionalInfo = ({ record }: { record: Company }) => {
  if (
    !record.created_at &&
    !record.organization_member_id &&
    !record.description &&
    !record.context_links
  ) {
    return null;
  }
  const getBaseURL = (url: string) => {
    const urlObject = new URL(url.startsWith("http") ? url : `https://${url}`);
    return urlObject.hostname;
  };

  return (
    <AsideSection title="Additional Info">
      {record.description && (
        <p className="text-sm  mb-1">{record.description}</p>
      )}
      {record.context_links && (
        <div className="flex flex-col">
          {record.context_links.map((link, index) =>
            link ? (
              <a
                key={index}
                className="link-action text-sm mb-1"
                href={link.startsWith("http") ? link : `https://${link}`}
                target="_blank"
                rel="noopener noreferrer"
                title={link}
              >
                {getBaseURL(link)}
              </a>
            ) : null,
          )}
        </div>
      )}
      {record.organization_member_id !== null && (
        <div className="inline-flex text-sm text-muted-foreground mb-1">
          Followed by&nbsp;
          <ReferenceField
            source="organization_member_id"
            reference="organization_members"
            record={record}
          >
            <OrganizationMemberName />
          </ReferenceField>
        </div>
      )}
      {record.created_at && (
        <p className="text-sm text-muted-foreground mb-1">
          Added on{" "}
          <DateField
            source="created_at"
            record={record}
            options={{
              year: "numeric",
              month: "long",
              day: "numeric",
            }}
          />
        </p>
      )}
    </AsideSection>
  );
};
